/**
 * MountTargetPicker — multi-select chip picker for mount targets.
 *
 * UX (matches the spec):
 *   * Click "Add mount" → a portal-rendered popover lists every suggested
 *     target for the current network (``@Network:id``, ``@Agent:id:name``,
 *     ``@Pool:id:pool``, ``@AgentPool:id:name:pool``, optional ``@Org``).
 *   * Type-ahead filters the list. Enter / click commits.
 *   * Selected mounts render as removable chips above the trigger button.
 *   * Labels mirror storage scope: ``@Agent:{networkId}:{agent}``,
 *     ``@AgentPool:{networkId}:{agent}:{pool}`` — same network axis as ``@Pool``.
 *
 * Why a PortalMenu? The mount picker is used inside the Configure Asset
 * modal **and** inline on each catalog row. Both live in scroll containers;
 * an in-flow ``absolute`` dropdown would be clipped. See
 * ``.cursor/rules/frontend-dropdown-portal.mdc``.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { PortalMenu } from '../PortalMenu';
import MountChip from './MountChip.jsx';
import {
  MOUNT_SUGGEST_AGENT_POOL_ONLY,
  MOUNT_SUGGEST_POOL_AXIS,
  SCOPES,
  findCoveringMount,
  suggestTargets,
  targetCovers,
  targetKey,
  targetLabel,
  validateTarget,
} from './MountTarget';

/**
 * @param {{
 *   value: object[],
 *   onChange: (next: object[]) => void,
 *   networkId?: string,
 *   agentNames?: string[],
 *   poolIds?: string[],
 *   includeOrg?: boolean,
 *   mountSuggestMode?: string,
 *   disabled?: boolean,
 * }} props
 */
export default function MountTargetPicker({
  value = [],
  onChange,
  networkId,
  agentNames = [],
  poolIds = [],
  includeOrg = false,
  mountSuggestMode,
  disabled = false,
  // The asset's FULL mount set across ALL scopes/lanes, used ONLY for the pyramid
  // coverage check — so a target already covered by a higher tag (e.g. @Tenant) is
  // greyed out / rejected even when ``value`` (the dialog's editable subset) is
  // lane- or network-filtered and doesn't itself contain the covering tag. Defaults
  // to ``value`` for back-compat (callers that edit the full set need not pass it).
  coverageMounts = null,
}) {
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState(null);

  const suggestions = useMemo(
    () =>
      suggestTargets({
        networkId,
        agentNames,
        poolIds,
        includeOrg,
        mountSuggestMode,
      }),
    [networkId, agentNames, poolIds, includeOrg, mountSuggestMode],
  );

  const selectedKeys = useMemo(
    () => new Set((value || []).map((t) => targetKey(t))),
    [value],
  );

  // Coverage is checked against the asset's FULL mount set (all scopes/lanes), not
  // just the editable ``value`` — so an option covered by a higher tag the dialog
  // isn't showing (e.g. an @Tenant mount while editing a network lane) is still
  // suppressed. Mirrors the backend ``find_covering_mount`` 409 so the UI freezes
  // it upfront instead of failing on save.
  const coverageSet = useMemo(
    () => (Array.isArray(coverageMounts) ? coverageMounts : (value || [])),
    [coverageMounts, value],
  );

  // Filter suggestions by query, drop already-selected entries, and hide rows
  // that a higher tag already covers — picking @Tenant means @Network/@Pool/etc.
  // below are moot, so we don't tempt the user into making a duplicate context.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return suggestions
      .filter((t) => !selectedKeys.has(targetKey(t)))
      .filter((t) => !findCoveringMount(t, coverageSet))
      .filter((t) => !q || targetLabel(t).toLowerCase().includes(q));
  }, [suggestions, selectedKeys, coverageSet, query]);

  const commit = (target) => {
    // Validate uniqueness against the editable set, AND coverage against the full
    // set, so a covered add is rejected with the backend's reasoning.
    const err = validateTarget(target, value) || (
      findCoveringMount(target, coverageSet)
        ? `Already covered by ${targetKey(findCoveringMount(target, coverageSet))} — remove the higher tag first or skip this one.`
        : null
    );
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    /** Promoting to a higher scope removes children it now covers — Save will diff and detach them. */
    const reduced = (value || []).filter((t) => !targetCovers(target, t?.target ?? t));
    onChange?.([...reduced, target]);
    setQuery('');
    // Keep the menu open so the user can pile on multiple chips without
    // clicking the trigger again — matches the Notion / Linear pattern.
    inputRef.current?.focus();
  };

  const remove = (target) => {
    const k = targetKey(target);
    onChange?.((value || []).filter((t) => targetKey(t) !== k));
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(value || []).map((t) => (
        <MountChip key={targetKey(t)} target={t} onRemove={disabled ? undefined : remove} />
      ))}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setError(null);
        }}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-dashed border-gray-300 text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Plus className="h-3 w-3" aria-hidden /> Add mount
      </button>

      <PortalMenu
        anchorRef={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        align="bottom-left"
        width={300}
        className="rounded-lg border border-gray-200 bg-white shadow-lg"
      >
        <div className="flex flex-col">
          <div className="border-b border-gray-100 p-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              autoFocus
              placeholder={
                mountSuggestMode === MOUNT_SUGGEST_POOL_AXIS || mountSuggestMode === MOUNT_SUGGEST_AGENT_POOL_ONLY
                  ? '@Pool or @AgentPool…'
                  : '@Network, @Agent, @Pool…'
              }
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filtered[0]) {
                  e.preventDefault();
                  commit(filtered[0]);
                }
              }}
              className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-500">No matching targets.</li>
            ) : (
              filtered.map((t) => (
                <li key={targetKey(t)}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => commit(t)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50"
                  >
                    <span className="truncate">{targetLabel(t)}</span>
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">
                      {labelForScope(t.scope)}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {error ? (
            <div className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-red-600">
              {error}
            </div>
          ) : null}
        </div>
      </PortalMenu>
    </div>
  );
}

function labelForScope(scope) {
  switch (scope) {
    case SCOPES.ORG:
      // UI label only; backend scope stays ``ORG`` (tenant-wide).
      return 'Tenant';
    case SCOPES.NETWORK:
      return 'Network';
    case SCOPES.POOL:
      return 'Pool';
    case SCOPES.AGENT:
      return 'Agent';
    case SCOPES.AGENT_POOL:
      return 'Agent + Pool';
    default:
      return '';
  }
}
